import { Injectable } from '@angular/core';
import { ProjectElementStore } from './project-element-store';
import { ProjectSubmoduleStore } from '../project-submodule/project-submodule-store';
import { ProjectModuleStore } from '../project-module/project-module-store';
import { ModalService } from '../../../services/modal/modal.service';
import { TranslateService } from '../../../utilities/translate/translate.service';
import { ProjectElement, ElementLog } from '../../../resources/element/project-element.interface';

@Injectable({
  providedIn: 'root'
})
export class ProjectElementService {

  constructor(
    private modalService: ModalService,
    private projectElementStore: ProjectElementStore,
    private projectModuleStore: ProjectModuleStore,
    private projectSubmoduleStore: ProjectSubmoduleStore,
    private translateService: TranslateService
  ) { }

  /**
   * Shows a confirmation modal and then set the matching element to .active = false.
   *
   * @param id Element to delete.
   */
  public delete(id: ProjectElement['_id']): void {
    this.modalService.confirm({
      title: this.translateService.get('deletespecificationtitle'),
      content: this.translateService.get('areyousuredeletespecification'),
      action: this.translateService.getPlural('delete'),
      actionIcon: 'delete',
      type: 'warn'
    }).afterClosed().subscribe((click: any) => {
      if (click === 'confirm') { // click var is always set in English
        this.projectElementStore.delete(id);
        this.projectModuleStore.refresh();
        this.projectSubmoduleStore.refresh();
      }
    });
  }

  /**
   * Assign a user to an element.
   *
   * @param element The element to assign the user to.
   * @param user The user to assign to the element.
   */
  public assignTo(element: ProjectElement, user: ProjectElement['assignedToUserId']): void {
    this.projectElementStore.update({
      ...element,
      assignedToUserId: user._id || -1
    });
  }

  /**
   * Get logs for specified element.
   *
   * @todo Use API to get logs.
   *
   * @param id Element id.
   * @returns Logs for specified element.
   */
  public getLogs(id: ProjectElement['_id']): ElementLog[] {
    return this.projectElementStore.queryOne(id).logs;
  }

  /**
   * Given an element it shows the rename modal and propagates a rename action.
   *
   * @rutger Iets aangepast vanwege:
   *  - Werkt net als `this.delete()` enkel met id, haal item op om te voorkomen dat je met referentie werkt.
   *  - Als je direct `name` opgeeft, wordt de modal geskipt, handig voor bijv inline edits.
   *
   * @param id Element id.
   * @param [name] Optional name. When not given, a rename modal prompt is triggered.
   */
  public rename(id: ProjectElement['_id'], name?: ProjectElement['name']): void {
    const element = this.projectElementStore.queryOne(id);
    if (!name) {
      this.modalService.prompt(
        this.translateService.get('rename'),
        element.name
      ).afterClosed().subscribe((response: any) => {
        if (response !== null) {
          this.projectElementStore.update({ _id: element._id, name: response });
        }
      });
    } else {
      this.projectElementStore.update({ _id: element._id, name: name });
    }
  }

  /**
   * Set element status.
   *
   * @param element The element to set the status for.
   * @param status The status to assign to the element.
   */
  public setStatus(element: ProjectElement, status: ProjectElement['status']): void {
    this.projectElementStore.update({
      ...element,
      status: status
    });
  }

}
